import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    const maxAttempts = 5;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/checkout/status/${sessionId}`, { withCredentials: true });
        setPaymentData(data);
        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (data.status === 'expired') {
          setStatus('error');
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        setStatus('error');
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div data-testid="payment-loading">
            <Loader2 size={48} className="text-amber-400 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
            <p className="text-sm text-white/40">Please wait while we confirm your payment.</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="payment-confirmed">
            <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-sm text-white/40 mb-6">Thank you for your purchase. Your products are ready for download.</p>
            {paymentData && (
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 mb-6 text-left space-y-3">
                <div>
                  <p className="text-xs text-white/30 mb-1">Amount</p>
                  <p className="text-lg font-bold text-white">${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</p>
                </div>
                {sessionId && (
                  <div>
                    <p className="text-xs text-white/30 mb-1">Order Reference</p>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(sessionId);
                          toast.success('Order reference copied', { description: 'Keep this for support', duration: 2500 });
                        } catch { toast.error('Could not copy'); }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-400 hover:text-amber-300 transition-colors cursor-pointer break-all text-left"
                      title="Click to copy — quote this if you contact support"
                      data-testid="copy-order-ref"
                    >
                      <span className="break-all">{sessionId}</span>
                      <Copy size={12} className="shrink-0 opacity-70" />
                    </button>
                    <p className="mt-1 text-[10px] text-white/30">Save this reference if you need to contact support</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/my-courses" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 transition-colors" data-testid="go-to-my-courses">
                Go to My Courses
              </Link>
              <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 text-white/70 text-sm font-semibold hover:border-white/20 transition-colors" data-testid="back-to-products">
                Continue Shopping
              </Link>
            </div>
          </div>
        )}
        {(status === 'error' || status === 'timeout') && (
          <div data-testid="payment-error">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              {status === 'timeout' ? 'Payment Status Unknown' : 'Payment Issue'}
            </h1>
            <p className="text-sm text-white/40 mb-6">
              {status === 'timeout' ? 'We couldn\'t confirm your payment. Please check your email for confirmation.' : 'There was an issue with your payment. Please try again.'}
            </p>
            <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 text-white/60 text-sm hover:border-white/20 transition-colors" data-testid="retry-shopping">
              Back to Products
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
